import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { RouterProvider } from '@tanstack/react-router'
import { de, enUS } from 'date-fns/locale'
import { type ReactNode, StrictMode, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { setAuthToken } from '@/api/mutator/custom-instance'
import {
	getLoginRecoveryRedirectHref,
	redirectToLoginOnce,
} from '@/bootstrap/login-recovery'
import { filterIconsByNames, IconSpriteProvider } from '@/components/icons'
import { UserSessionProvider, useUserSession } from '@/context/user-session'
import {
	AppAbilityProvider,
	createAppAbility,
} from '@/integrations/casl/ability'
import {
	AuthProvider as OidcAuthProvider,
	useOidcAuth,
} from '@/integrations/oidc/AuthProvider'
import { getLocale } from '@/integrations/paraglide/runtime'
import { AppConfigProvider } from '../components/AppConfigProvider'
import { useAppConfig } from '../hooks/use-appConfig'
import { MuiThemeProvider } from '../integrations/mui/mui-theme-provider'
import * as TanstackQuery from '../integrations/tanstack-query/root-provider'
import type { AppRouter } from '../lib/router'

const icons = filterIconsByNames(
	'user', // 'user' icon is provided by the AccountBox component
	'house', // 'house' icon is provided by the Breadcrumb component
	'chevronDown', // 'chevronDown' icon is provided by the Breadcrumb component
	'chevronRight', // 'chevronRight' icon is provided by the Breadcrumb component
	'chevronUp', // 'chevronUp' icon is provided by the Breadcrumb component
	'globe', // 'globe' icon is provided by the LanguageSwitcher component
	'search', // 'search' icon is provided by the SearchBox component
	'save', // 'save' icon is used in various places for save actions
	'loader', // 'loader' icon is used in various places to indicate loading state
	'trash', // 'trash' icon is used in various places for delete actions
	'undo', // 'undo' icon is used in the CustomColorPicker component to reset to default color
	'pencil', // 'pencil' icon is used in various places for edit actions
	'image', // 'image' icon is used in the CustomFileUpload component as a placeholder when no image is uploaded
	'boxes', // 'boxes' icon is used in the AccountsTable component to navigate to the tenants of an account
	'shieldUser', // 'shieldUser' icon is used in the AccountSecurity component to indicate security-related settings
	'check', // 'check' icon is used in various places to indicate success or completion
)

interface AppRouterProviderProps {
	readonly router: AppRouter
}

function DatePickerLocalizationProvider({
	children,
}: Readonly<{ children: ReactNode }>) {
	const locale = getLocale()
	return (
		<LocalizationProvider
			dateAdapter={AdapterDateFns}
			adapterLocale={locale.startsWith('de') ? de : enUS}
		>
			{children}
		</LocalizationProvider>
	)
}

export function AppRouterProvider({
	router,
}: Readonly<AppRouterProviderProps>) {
	const auth = useOidcAuth()
	const config = useAppConfig()
	useEffect(() => {
		setAuthToken(auth.user?.access_token)
	}, [auth.user?.access_token])
	const userSession = useUserSession()
	const loginRecoveryRedirectHref = getLoginRecoveryRedirectHref({
		basePath: globalThis.__BASE_URL,
		isOidcEnabled: Boolean(config.oidc),
		location: globalThis.location,
		loginRequired: userSession.loginRequired,
	})

	useEffect(() => {
		if (!loginRecoveryRedirectHref) return

		// Browser navigation is required here because the router is still gated on
		// bootstrap readiness, and the stored return target must be stripped back to
		// a router-relative path so nested base-path deployments can restore it.
		redirectToLoginOnce(loginRecoveryRedirectHref)
	}, [loginRecoveryRedirectHref])

	const ability = useMemo(
		() => createAppAbility(userSession.permissions),
		[userSession.permissions],
	)
	// Show loading while auth or permission session is still resolving.
	if (
		auth.isLoading ||
		!userSession.isReady ||
		Boolean(loginRecoveryRedirectHref)
	) {
		return <p>Ladevorgang …</p>
	}
	return (
		<AppAbilityProvider value={ability}>
			<RouterProvider
				router={router}
				context={{ auth, config, ability, userSession }}
				basepath={globalThis.__BASE_URL}
			/>
		</AppAbilityProvider>
	)
}

function AppRouterWithSession({ router }: Readonly<AppRouterProviderProps>) {
	const auth = useOidcAuth()

	return (
		<UserSessionProvider
			authReady={!auth.isLoading}
			accessToken={auth.user?.access_token}
		>
			<AppRouterProvider router={router} />
		</UserSessionProvider>
	)
}

export interface RenderAppOptions {
	readonly createRoot?: typeof ReactDOM.createRoot
}

export function renderApp(
	rootElement: HTMLElement | null,
	router: AppRouter,
	options: RenderAppOptions = {},
) {
	if (!rootElement) return
	// Prevent mounting twice (e.g. if SSR/hydration inserted markup already)
	if (rootElement.innerHTML) return

	const AuthProvider = OidcAuthProvider

	const createRoot = options.createRoot ?? ReactDOM.createRoot
	const root = createRoot(rootElement)
	root.render(
		<StrictMode>
			<TanstackQuery.Provider>
				<AppConfigProvider>
					<AuthProvider>
						<DatePickerLocalizationProvider>
							<MuiThemeProvider>
								<IconSpriteProvider icons={icons}>
									<AppRouterWithSession router={router} />
								</IconSpriteProvider>
							</MuiThemeProvider>
						</DatePickerLocalizationProvider>
					</AuthProvider>
				</AppConfigProvider>
			</TanstackQuery.Provider>
		</StrictMode>,
	)
}
